import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { CheckCircle2, Eye, EyeOff, PlugZap, Plus, Trash2, XCircle } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fetcher } from "@/lib/api";

const EMPTY = {
  name: "", domain: "", description: "", editorial_line: "", categories: [],
  accent_color: "#E11D48", logo_url: "", site_url: "",
  wordpress: { url: "", username: "", app_password: "" },
  social: {},
  messaging: { telegram_chat_id: "", telegram_bot_token: "", whatsapp_recipients: "" },
  ai_model: "",
  social_api: {
    facebook_page_id: "", facebook_token: "", instagram_user_id: "", instagram_token: "",
    threads_user_id: "", threads_token: "",
  },
  card_templates: [],
  active: true,
};

const NETWORKS = ["facebook", "instagram", "x", "youtube", "linkedin", "tiktok", "threads", "whatsapp", "telegram"];

export default function Medios() {
  const { data, mutate } = useSWR("/outlets", fetcher);
  const { data: models } = useSWR("/ai/models", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [showPass, setShowPass] = useState(false);
  const [testing, setTesting] = useState("");
  const [result, setResult] = useState(null);

  const runTest = async (outletId, name) => {
    setTesting(outletId);
    setResult(null);
    try {
      const { data: r } = await api.get(`/outlets/${outletId}/test`);
      setResult({ ...r, outletId, name });
      r.ok ? toast.success(`${name}: conexión y permisos correctos`)
           : toast.error(`${name}: revise el detalle de la prueba`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTesting("");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setWp = (k, v) => setForm((f) => ({ ...f, wordpress: { ...(f.wordpress || {}), [k]: v } }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social: { ...(f.social || {}), [k]: v } }));
  const setMsg = (k, v) => setForm((f) => ({ ...f, messaging: { ...(f.messaging || {}), [k]: v } }));
  const setApi = (k, v) => setForm((f) => ({ ...f, social_api: { ...(f.social_api || {}), [k]: v } }));

  const uploadTemplate = async (file) => {
    if (!form.id) return toast.error("Guarde el medio antes de subir plantillas");
    const body = new FormData();
    body.append("file", file);
    body.append("name", file.name.replace(/\.[^.]+$/, ""));
    body.append("format", "4:5");
    try {
      const { data: tpl } = await api.post(`/outlets/${form.id}/templates`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, card_templates: [...(f.card_templates || []), tpl] }));
      toast.success("Plantilla añadida; ajuste la zona del titular si es necesario");
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeTemplate = async (templateId) => {
    try {
      await api.delete(`/outlets/${form.id}/templates/${templateId}`);
      setForm((f) => ({
        ...f, card_templates: (f.card_templates || []).filter((t) => t.id !== templateId),
      }));
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const socialTest = async (outletId, name) => {
    setTesting(`social-${outletId}`);
    setResult(null);
    try {
      const { data: r } = await api.get(`/outlets/${outletId}/social-test`);
      setResult({ ...r, outletId, name });
      r.ok ? toast.success(`${name}: redes conectadas`) : toast.error(`${name}: revise el detalle`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTesting("");
    }
  };

  const save = async () => {
    const payload = {
      name: form.name, domain: form.domain, description: form.description,
      editorial_line: form.editorial_line, categories: form.categories,
      accent_color: form.accent_color, logo_url: form.logo_url, site_url: form.site_url,
      wordpress: form.wordpress, social: form.social || {}, messaging: form.messaging || {},
      social_api: form.social_api || {}, ai_model: form.ai_model || "", active: form.active,
    };
    try {
      if (form.id) await api.put(`/outlets/${form.id}`, payload);
      else await api.post("/outlets", payload);
      toast.success("Medio guardado");
      setOpen(false);
      setForm(EMPTY);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader
        kicker="Multimedios"
        title="Medios digitales"
        description="Configure cada medio, su línea editorial, categorías y credenciales de WordPress (JNews) para la publicación automática."
      >
        <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-outlet-btn">
          <Plus className="h-4 w-4 mr-2" />Nuevo medio
        </Button>
      </PageHeader>

      <div className="grid md:grid-cols-2 gap-4" data-testid="outlets-grid">
        {(data || []).map((o) => (
          <div key={o.id} className="border border-border bg-card p-6" data-testid={`outlet-${o.id}`}>
            {o.logo_url && (
              <img src={o.logo_url} alt={o.name} className="h-12 w-auto max-w-[260px] object-contain mb-4" />
            )}
            <div className="flex items-center gap-2 mb-2">
              <span className="h-3 w-3 rounded-full" style={{ background: o.accent_color }} />
              <Badge variant={o.active ? "default" : "secondary"}>{o.active ? "Activo" : "Inactivo"}</Badge>
              {o.wordpress?.app_password ? (
                <Badge variant="outline">WordPress conectado</Badge>
              ) : (
                <Badge variant="outline" className="text-destructive border-destructive/40">WordPress sin credenciales</Badge>
              )}
            </div>
            <h3 className="text-2xl font-bold">{o.name}</h3>
            <p className="text-sm text-muted-foreground mt-1">{o.description}</p>
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{o.editorial_line}</p>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {[...new Set(o.categories || [])].map((c) => <Badge key={c} variant="secondary">{c}</Badge>)}
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              <Button size="sm" variant="outline" onClick={() => { setForm({ ...EMPTY, ...o }); setShowPass(false); setOpen(true); }} data-testid={`edit-outlet-${o.id}`}>
                Configurar
              </Button>
              <Button size="sm" variant="outline" onClick={() => runTest(o.id, o.name)} disabled={testing === o.id} data-testid={`test-outlet-${o.id}`}>
                <PlugZap className="h-3.5 w-3.5 mr-1.5" />
                {testing === o.id ? "Probando…" : "Probar publicación"}
              </Button>
              <Button size="sm" variant="outline" onClick={async () => { await api.delete(`/outlets/${o.id}`).catch((e) => toast.error(errMsg(e))); mutate(); }} data-testid={`delete-outlet-${o.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            {result && result.outletId === o.id && (
              <ul className="mt-4 space-y-1.5 border-t border-border pt-3" data-testid={`test-result-${o.id}`}>
                {result.checks.map((c) => (
                  <li key={c.name} className="flex items-start gap-2 text-xs">
                    {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                          : <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />}
                    <span><strong>{c.name}:</strong> {c.detail}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <div className="border border-border bg-card p-6 mt-6" data-testid="jnews-panel">
        <div className="kicker mb-2">Campos de la plantilla JNews</div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          La app escribe directamente en los campos propios de JNews al publicar: <strong>subtítulo</strong>
          {" "}(<code className="mono text-xs">jnews_single_post.subtitle</code>),
          {" "}<strong>categoría principal</strong> (<code className="mono text-xs">jnews_primary_category</code>),
          {" "}<strong>extracto</strong>, imagen destacada, categoría y etiquetas. No requiere plugins adicionales.
        </p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="outlet-dialog">
          <DialogHeader><DialogTitle className="serif text-2xl">Medio digital</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="outlet-name-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Dominio</Label>
              <Input value={form.domain} onChange={(e) => set("domain", e.target.value)} className="mt-1.5" data-testid="outlet-domain-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Descripción</Label>
              <Input value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" data-testid="outlet-description-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Línea editorial y audiencia</Label>
              <Textarea rows={3} value={form.editorial_line} onChange={(e) => set("editorial_line", e.target.value)} className="mt-1.5" data-testid="outlet-editorial-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Categorías (coma)</Label>
              <Input value={(form.categories || []).join(", ")}
                onChange={(e) => set("categories", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                className="mt-1.5" data-testid="outlet-categories-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Logo del medio (URL)</Label>
              <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} className="mt-1.5" data-testid="outlet-logo-input" />
              {form.logo_url && <img src={form.logo_url} alt="" className="h-10 mt-2 object-contain" />}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider">Sitio web</Label>
                <Input value={form.site_url} onChange={(e) => set("site_url", e.target.value)} className="mt-1.5" data-testid="outlet-site-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Color de acento</Label>
                <Input value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="mt-1.5" data-testid="outlet-accent-input" /></div>
            </div>
            <div className="border-t border-border pt-3">
              <div className="kicker mb-2">Redes sociales del medio (para el cierre del boletín)</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {NETWORKS.map((net) => (
                  <Input
                    key={net}
                    placeholder={net}
                    value={form.social?.[net] || ""}
                    onChange={(e) => setSocial(net, e.target.value)}
                    data-testid={`outlet-social-${net}-input`}
                  />
                ))}
              </div>
            </div>
            <div className="border-t border-border pt-3">
              <div className="kicker mb-2">WordPress (JNews) — REST API</div>
              <Input placeholder="https://midominio.do" value={form.wordpress?.url || ""} onChange={(e) => setWp("url", e.target.value)} className="mb-2" data-testid="wp-url-input" />
              <Input placeholder="Usuario de WordPress" value={form.wordpress?.username || ""} onChange={(e) => setWp("username", e.target.value)} className="mb-2" data-testid="wp-user-input" />
              <div className="relative">
                <Input
                  type={showPass ? "text" : "password"}
                  placeholder="Application Password"
                  value={form.wordpress?.app_password || ""}
                  onChange={(e) => setWp("app_password", e.target.value)}
                  className="pr-10"
                  data-testid="wp-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPass ? "Ocultar contraseña" : "Ver contraseña"}
                  data-testid="toggle-wp-password"
                >
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Genere la contraseña en WordPress: Usuarios → Perfil → Contraseñas de aplicación.
                El usuario debe tener rol <strong>Editor</strong> o <strong>Administrador</strong> para publicar.
              </p>
              {form.id && (
                <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => runTest(form.id, form.name)} disabled={testing === form.id} data-testid="test-outlet-dialog-btn">
                  <PlugZap className="h-3.5 w-3.5 mr-1.5" />
                  {testing === form.id ? "Probando…" : "Probar conexión y permisos"}
                </Button>
              )}
              {result && form.id && result.outletId === form.id && (
                <ul className="mt-3 space-y-1.5" data-testid="dialog-test-result">
                  {result.checks.map((c) => (
                    <li key={c.name} className="flex items-start gap-2 text-xs">
                      {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                            : <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />}
                      <span><strong>{c.name}:</strong> {c.detail}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-t border-border pt-3">
              <div className="kicker mb-2">Boletín por WhatsApp y Telegram</div>
              <p className="text-xs text-muted-foreground mb-2">
                Destino del envío del boletín resumido. Si se deja vacío se usan los datos globales de Ajustes.
              </p>
              <Input placeholder="Chat o canal de Telegram (ej. @paginaunodo o -1001234567890)"
                value={form.messaging?.telegram_chat_id || ""}
                onChange={(e) => setMsg("telegram_chat_id", e.target.value)}
                className="mb-2" data-testid="msg-telegram-chat-input" />
              <Input placeholder="Token del bot de Telegram (opcional, propio del medio)"
                value={form.messaging?.telegram_bot_token || ""}
                onChange={(e) => setMsg("telegram_bot_token", e.target.value)}
                className="mb-2" data-testid="msg-telegram-token-input" />
              <Input placeholder="Destinatarios de WhatsApp separados por coma (18091234567)"
                value={typeof form.messaging?.whatsapp_recipients === "string"
                  ? form.messaging.whatsapp_recipients
                  : (form.messaging?.whatsapp_recipients || []).join(", ")}
                onChange={(e) => setMsg("whatsapp_recipients", e.target.value)}
                data-testid="msg-whatsapp-recipients-input" />
            </div>
            <div className="border-t border-border pt-3">
              <div className="kicker mb-2">Modelo de IA de este medio</div>
              <select
                className="w-full h-10 border border-input bg-background px-3 text-sm rounded-md"
                value={form.ai_model || ""}
                onChange={(e) => set("ai_model", e.target.value)}
                data-testid="outlet-ai-model-select"
              >
                <option value="">Usar el modelo configurado en Ajustes</option>
                {(models?.models || []).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>

            <div className="border-t border-border pt-3">
              <div className="kicker mb-2">Redes con API (publicación automática)</div>
              <p className="text-xs text-muted-foreground mb-2">
                Cree una app en developers.facebook.com y pegue aquí los datos. Threads usa un token
                propio, distinto al de Facebook.
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input placeholder="ID de la página de Facebook" value={form.social_api?.facebook_page_id || ""}
                  onChange={(e) => setApi("facebook_page_id", e.target.value)} data-testid="api-fb-page-input" />
                <Input type={showPass ? "text" : "password"} placeholder="Page Access Token"
                  value={form.social_api?.facebook_token || ""}
                  onChange={(e) => setApi("facebook_token", e.target.value)} data-testid="api-fb-token-input" />
                <Input placeholder="ID de la cuenta de Instagram profesional"
                  value={form.social_api?.instagram_user_id || ""}
                  onChange={(e) => setApi("instagram_user_id", e.target.value)} data-testid="api-ig-id-input" />
                <Input type={showPass ? "text" : "password"} placeholder="Token de Instagram (opcional, usa el de FB)"
                  value={form.social_api?.instagram_token || ""}
                  onChange={(e) => setApi("instagram_token", e.target.value)} data-testid="api-ig-token-input" />
                <Input placeholder="ID de usuario de Threads" value={form.social_api?.threads_user_id || ""}
                  onChange={(e) => setApi("threads_user_id", e.target.value)} data-testid="api-threads-id-input" />
                <Input type={showPass ? "text" : "password"} placeholder="Token de Threads"
                  value={form.social_api?.threads_token || ""}
                  onChange={(e) => setApi("threads_token", e.target.value)} data-testid="api-threads-token-input" />
              </div>
              {form.id && (
                <Button type="button" variant="outline" size="sm" className="mt-3"
                  onClick={() => socialTest(form.id, form.name)}
                  disabled={testing === `social-${form.id}`} data-testid="test-social-dialog-btn">
                  <PlugZap className="h-3.5 w-3.5 mr-1.5" />
                  {testing === `social-${form.id}` ? "Probando…" : "Probar redes"}
                </Button>
              )}
            </div>

            <div className="border-t border-border pt-3">
              <div className="kicker mb-2">Plantillas para redes (1080×1350)</div>
              <p className="text-xs text-muted-foreground mb-2">
                Suba el PNG con la zona de la foto transparente; el titular se monta automáticamente.
              </p>
              <div className="space-y-2">
                {(form.card_templates || []).map((t) => (
                  <div key={t.id} className="flex items-center gap-3 border border-border p-2"
                       data-testid={`template-${t.id}`}>
                    <img src={t.url} alt={t.name} className="h-16 w-auto object-contain bg-muted" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{t.name}</p>
                      <p className="text-xs mono text-muted-foreground">{t.format}</p>
                    </div>
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => removeTemplate(t.id)} data-testid={`delete-template-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => e.target.files?.[0] && uploadTemplate(e.target.files[0])}
                className="mt-3 text-sm"
                data-testid="template-upload-input"
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="outlet-active-switch" />
              <span className="text-sm">Medio activo</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!form.name} data-testid="save-outlet-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
